import { z } from 'zod'
import { type Expand } from '@/types/ui/expand'
import { zodValidationHelpers } from '@/lib/utils/zod-validation-helpers'
import { ENGAGEMENT_BILLING_TYPE } from '@/lib/constants/engagement-codes'

// Base schema without conditional validation
const baseInvoiceFormSchema = z.object({
  selectedAlternateBill: z.string().nullable(),
  selectedBillingFrequency: z.string(),
  selectedPaymentTerms: z.string(),
  selectedInvoiceTemplate: z.string(),
  selectedInvoiceRecipient: z.number(),
  selectedDeliveryMethod: z.string(),
  includeTimesheet: z.boolean().default(true),
  invoiceNotes: z.array(z.object({ value: z.string().max(128, 'Maximum 128 characters allowed') })).max(4, 'Maximum 4 notes allowed').default([]),
  ccEmails: z.array(z.string()).default([]),
})

// Factory function to create schema with conditional validation based on billing type and whether accounting fields are required
export const createInvoiceFormSchema = (billingType?: string, requireAccountingFields: boolean = false) => {
  return baseInvoiceFormSchema.superRefine((data, ctx) => {
    // Only apply validation when accounting fields are required
    if (requireAccountingFields) {
      // Payment terms is required
      if (!data.selectedPaymentTerms || data.selectedPaymentTerms.trim() === '') {
        zodValidationHelpers.requiredString(ctx, ['selectedPaymentTerms'])
      }
      
      // Invoice template is required
      if (!data.selectedInvoiceTemplate || data.selectedInvoiceTemplate.trim() === '') {
        zodValidationHelpers.requiredString(ctx, ['selectedInvoiceTemplate'])
      }
      
      // Invoice recipient is required
      if (!data.selectedInvoiceRecipient || data.selectedInvoiceRecipient <= 0) {
        zodValidationHelpers.minNumber(ctx, ['selectedInvoiceRecipient'], 1, 'Field is required')
      }
      
      // Delivery method is required
      if (!data.selectedDeliveryMethod || data.selectedDeliveryMethod.trim() === '') {
        zodValidationHelpers.requiredString(ctx, ['selectedDeliveryMethod'])
      }
      
      // Billing frequency is only required for non-milestone billing types
      if (billingType !== ENGAGEMENT_BILLING_TYPE.MILESTONE) {
        if (!data.selectedBillingFrequency || data.selectedBillingFrequency.trim() === '') {
          zodValidationHelpers.requiredString(ctx, ['selectedBillingFrequency'])
        }
      }
    }

    // Validate CC emails when delivery method is Soft Copy
    if (data.selectedDeliveryMethod === 'SoftCopy' && data.ccEmails.length > 0) {
      const emailSchema = z.string().email()
      const hasInvalidEmail = data.ccEmails.some(email => !emailSchema.safeParse(email).success)
      if (hasInvalidEmail) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Warning: one or more emails is invalid',
          path: ['ccEmails'],
        })
      }
    }
  })
}

// Automatically infer the TypeScript type from the schema
export type InvoiceFormData = Expand<z.infer<typeof baseInvoiceFormSchema>>
